import { useState } from 'react';
import { useApi } from '../hooks/useApi';
import { DataTable } from '../components/DataTable';
import { HelpTip } from '../components/HelpTip';
import {
  TIPOS_RETENCION_SUFRIDA, ETIQUETAS_TIPO_RETENCION_SUFRIDA, TIPO_RETENCION_OTRO, etiquetaTipoRetencionSufrida, type OrigenRetencionSufrida,
} from '../lib/retencionSufrida';

interface JurisdiccionFiscal {
  id: number;
  nombre: string;
  codigo: string;
  activo: boolean;
}

interface ReporteRetencionesRow {
  jurisdiccionId: number;
  jurisdiccion: string;
  periodo: string;
  tipo: string;
  totalRetenciones: number;
  totalPercepciones: number;
  /** Control: lo que calcula el sistema; el total es lo declarado en los comprobantes. */
  totalPercepcionesCalculadas?: number;
}

interface RetencionSufridaRow {
  /** Id de la retención (Cliente) o del cobro de MercadoPago (se repite si el pago tiene varias). */
  id: number;
  origen: OrigenRetencionSufrida;
  /** Las de MercadoPago: leídas de campos no verificados con pagos reales. */
  aVerificar: boolean;
  fecha: string;
  fechaCertificado: string | null;
  fechaCobro: string;
  clienteId: number | null;
  cliente: string;
  cuit: string | null;
  tipo: string;
  jurisdiccionId: number | null;
  jurisdiccion: string | null;
  /** Cliente: número del certificado. MercadoPago: id del pago. */
  numeroCertificado: string | null;
  /** MercadoPago: nombre de la retención tal como lo informa. */
  detalle: string | null;
  monto: number;
  cobroId: number | null;
  facturaVentaId: number | null;
  factura: string | null;
}

interface ReporteRetencionesSufridas {
  filas: RetencionSufridaRow[];
  totalesPorTipo: { tipo: string; total: number }[];
  totalesPorJurisdiccion: { jurisdiccionId: number; jurisdiccion: string; total: number }[];
  totalesPorOrigen?: { origen: OrigenRetencionSufrida; total: number }[];
  total: number;
  diferenciasSinIdentificar?: { total: number; pagos: number };
}

const AVISO_MERCADOPAGO_A_VERIFICAR = 'Datos de MercadoPago a verificar con el resumen de la cuenta';

type Pestania = 'propias' | 'sufridas';

const pesos = (n: number) => `$${Number(n).toFixed(2)}`;

export function RetencionesPage() {
  const { data: jurisdicciones } = useApi<JurisdiccionFiscal[]>('/api/v3/impositivo/jurisdicciones');
  const [pestania, setPestania] = useState<Pestania>('propias');

  const pestanias = (
    <div className="flex gap-1 border-b border-slate-200">
      {([['propias', 'Practicadas y percepciones'], ['sufridas', 'Sufridas']] as [Pestania, string][]).map(([id, label]) => (
        <button
          key={id}
          onClick={() => setPestania(id)}
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${pestania === id ? 'border-blue-600 text-blue-700' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
        >
          {label}
        </button>
      ))}
    </div>
  );

  if (pestania === 'sufridas') {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 inline">Retenciones y Percepciones</h1>
          <HelpTip
            tips={[
              'Retenciones sufridas: las que te hicieron los clientes al pagarte (se cargan en Cobros, con su certificado).',
              'También las que informa MercadoPago en los pagos de MercadoLibre (origen MercadoPago): se leen al actualizar los cobros de MercadoPago y hay que verificarlas con el resumen de la cuenta.',
              'Son un pago a cuenta de tus impuestos: IIBB por jurisdicción, IVA, Ganancias y SUSS.',
              'La fecha es la del certificado; si no se cargó, la del cobro. En las de MercadoPago, la de aprobación del pago.',
              'Exportá el detalle a CSV con el botón "Exportar CSV" de la tabla.',
            ]}
          />
          <p className="text-slate-500 text-sm mt-1">Retenciones que te hicieron los clientes y MercadoPago, con totales por tipo, jurisdicción y origen</p>
        </div>
        {pestanias}
        <RetencionesSufridas jurisdicciones={jurisdicciones ?? []} />
      </div>
    );
  }

  return <RetencionesPropias jurisdicciones={jurisdicciones ?? []} pestanias={pestanias} />;
}

function RetencionesSufridas({ jurisdicciones }: { jurisdicciones: JurisdiccionFiscal[] }) {
  const [desde, setDesde] = useState('');
  const [hasta, setHasta] = useState('');
  const [jurisdiccionId, setJurisdiccionId] = useState<number>(0);
  const [tipo, setTipo] = useState('');
  const [origen, setOrigen] = useState<'' | OrigenRetencionSufrida>('');

  const params = new URLSearchParams();
  if (desde) params.set('desde', desde);
  if (hasta) params.set('hasta', hasta);
  if (jurisdiccionId) params.set('jurisdiccionId', String(jurisdiccionId));
  if (tipo) params.set('tipo', tipo);
  if (origen) params.set('origen', origen);
  const query = params.toString();
  const endpoint = `/api/v3/impositivo/retenciones/sufridas${query ? `?${query}` : ''}`;

  const { data, loading, error } = useApi<ReporteRetencionesSufridas>(endpoint, [desde, hasta, jurisdiccionId, tipo, origen]);
  const hayMercadoPago = (data?.filas ?? []).some(f => f.origen === 'MercadoPago');
  const diferencias = data?.diferenciasSinIdentificar ?? { total: 0, pagos: 0 };

  const columns = [
    { key: 'fecha', header: 'Fecha', render: (r: RetencionSufridaRow) => (
      <span title={r.origen === 'MercadoPago' ? 'Fecha de aprobación del pago' : r.fechaCertificado ? 'Fecha del certificado' : 'Sin fecha de certificado: fecha del cobro'}>{r.fecha}</span>
    ) },
    { key: 'origen', header: 'Origen', render: (r: RetencionSufridaRow) => r.aVerificar
      ? <span className="text-amber-700 font-medium" title={AVISO_MERCADOPAGO_A_VERIFICAR}>{r.origen} (a verificar)</span>
      : r.origen },
    { key: 'cliente', header: 'Cliente' },
    { key: 'cuit', header: 'CUIT', render: (r: RetencionSufridaRow) => r.cuit ?? '—' },
    { key: 'tipo', header: 'Tipo', render: (r: RetencionSufridaRow) => (
      <span title={r.detalle ? `Informado por MercadoPago como "${r.detalle}"` : undefined}>
        {etiquetaTipoRetencionSufrida(r.tipo)}{r.detalle && <span className="text-slate-400 text-xs"> ({r.detalle})</span>}
      </span>
    ) },
    { key: 'jurisdiccion', header: 'Jurisdicción', render: (r: RetencionSufridaRow) =>
      r.jurisdiccion ?? (r.origen === 'MercadoPago' && r.tipo === 'IIBB' ? 'Sin identificar' : '—') },
    { key: 'numeroCertificado', header: 'Certificado / pago', render: (r: RetencionSufridaRow) =>
      r.numeroCertificado ? (r.origen === 'MercadoPago' ? `Pago ${r.numeroCertificado}` : r.numeroCertificado) : '—' },
    { key: 'monto', header: 'Monto', render: (r: RetencionSufridaRow) => pesos(r.monto) },
    { key: 'cobroId', header: 'Cobro', render: (r: RetencionSufridaRow) => r.cobroId ? `#${r.cobroId}` : '—' },
    { key: 'factura', header: 'Factura', render: (r: RetencionSufridaRow) =>
      r.origen === 'MercadoPago' ? '—' : r.factura ?? (r.facturaVentaId ? `#${r.facturaVentaId}` : 'A cuenta') },
  ];

  return (
    <>
      <div className="bg-white rounded-lg border border-slate-200 p-4">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Desde</label>
            <input type="date" className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm" value={desde} onChange={e => setDesde(e.target.value)} />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Hasta</label>
            <input type="date" className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm" value={hasta} onChange={e => setHasta(e.target.value)} />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Tipo</label>
            <select className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm" value={tipo} onChange={e => setTipo(e.target.value)}>
              <option value="">Todos</option>
              {TIPOS_RETENCION_SUFRIDA.map(t => <option key={t} value={t}>{ETIQUETAS_TIPO_RETENCION_SUFRIDA[t]}</option>)}
              <option value={TIPO_RETENCION_OTRO}>Otro (MercadoPago)</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Origen</label>
            <select className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm" value={origen} onChange={e => setOrigen(e.target.value as '' | OrigenRetencionSufrida)}>
              <option value="">Todos</option>
              <option value="Cliente">Clientes (Cobros)</option>
              <option value="MercadoPago">MercadoPago</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Jurisdicción</label>
            <select
              className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm"
              value={jurisdiccionId || ''}
              onChange={e => setJurisdiccionId(Number(e.target.value))}
            >
              <option value="">Todas</option>
              {jurisdicciones.map(j => <option key={j.id} value={j.id}>{j.nombre}</option>)}
            </select>
          </div>
        </div>
        {error && <p className="text-red-600 text-sm mt-2">{String(error)}</p>}
      </div>

      {hayMercadoPago && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
          <strong>{AVISO_MERCADOPAGO_A_VERIFICAR}.</strong> Se leen de los pagos de MercadoLibre (campos de MercadoPago todavía
          no verificados con pagos reales): compará cada retención con el resumen de la cuenta de MercadoPago antes de
          usarla en una declaración jurada.
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <div className="bg-white rounded-lg border border-slate-200 p-4">
          <p className="text-xs font-medium text-slate-500">Total retenido</p>
          <p className="text-2xl font-bold text-slate-800">{pesos(data?.total ?? 0)}</p>
          {(data?.totalesPorOrigen ?? []).length > 1 && (
            <ul className="text-xs text-slate-500 mt-1 space-y-0.5">
              {(data?.totalesPorOrigen ?? []).map(o => (
                <li key={o.origen} className="flex justify-between"><span>{o.origen}</span><span>{pesos(o.total)}</span></li>
              ))}
            </ul>
          )}
        </div>
        <div className="bg-white rounded-lg border border-slate-200 p-4">
          <p className="text-xs font-medium text-slate-500 mb-1">Por tipo</p>
          {(data?.totalesPorTipo ?? []).length === 0 ? <p className="text-sm text-slate-400">—</p> : (
            <ul className="text-sm space-y-0.5">
              {(data?.totalesPorTipo ?? []).map(t => (
                <li key={t.tipo} className="flex justify-between"><span>{etiquetaTipoRetencionSufrida(t.tipo)}</span><span>{pesos(t.total)}</span></li>
              ))}
            </ul>
          )}
        </div>
        <div className="bg-white rounded-lg border border-slate-200 p-4">
          <p className="text-xs font-medium text-slate-500 mb-1">IIBB por jurisdicción</p>
          {(data?.totalesPorJurisdiccion ?? []).length === 0 ? <p className="text-sm text-slate-400">—</p> : (
            <ul className="text-sm space-y-0.5">
              {(data?.totalesPorJurisdiccion ?? []).map(j => (
                <li key={j.jurisdiccionId} className="flex justify-between"><span>{j.jurisdiccion}</span><span>{pesos(j.total)}</span></li>
              ))}
            </ul>
          )}
        </div>
        <div className="bg-white rounded-lg border border-slate-200 p-4">
          <p className="text-xs font-medium text-slate-500 inline">Diferencias sin identificar</p>
          <HelpTip
            tips={[
              'Pagos de MercadoPago del período en los que el bruto menos lo acreditado es mayor que las comisiones más las retenciones informadas.',
              'Puede ser una retención que MercadoPago no informa en el pago, un envío o un cargo a revisar en el resumen de la cuenta.',
              'No se suma al total retenido.',
            ]}
          />
          <p className={`text-2xl font-bold ${diferencias.total > 0 ? 'text-amber-600' : 'text-slate-800'}`}>{pesos(diferencias.total)}</p>
          <p className="text-xs text-slate-400">{diferencias.pagos} {diferencias.pagos === 1 ? 'pago' : 'pagos'} de MercadoPago</p>
        </div>
      </div>

      <DataTable
        columns={columns}
        data={data?.filas ?? []}
        loading={loading}
        emptyMessage="Sin retenciones sufridas para los filtros seleccionados"
        title="Retenciones-sufridas"
      />
    </>
  );
}

function RetencionesPropias({ jurisdicciones, pestanias }: { jurisdicciones: JurisdiccionFiscal[]; pestanias: JSX.Element }) {

  const [periodo, setPeriodo] = useState('');
  const [jurisdiccionId, setJurisdiccionId] = useState<number>(0);
  const [tipo, setTipo] = useState('');

  const params = new URLSearchParams();
  if (periodo.trim()) params.set('periodo', periodo.trim());
  if (jurisdiccionId) params.set('jurisdiccionId', String(jurisdiccionId));
  if (tipo.trim()) params.set('tipo', tipo.trim());
  const query = params.toString();
  const endpoint = `/api/v3/impositivo/retenciones/reporte${query ? `?${query}` : ''}`;

  const { data, loading } = useApi<ReporteRetencionesRow[]>(endpoint, [periodo, jurisdiccionId, tipo]);

  const columns = [
    { key: 'jurisdiccion', header: 'Jurisdicción' },
    { key: 'periodo', header: 'Período' },
    { key: 'tipo', header: 'Tipo' },
    {
      key: 'totalRetenciones',
      header: 'Total Retenciones',
      render: (r: ReporteRetencionesRow) => `$${Number(r.totalRetenciones).toFixed(2)}`,
    },
    {
      key: 'totalPercepciones',
      header: 'Total Percepciones',
      render: (r: ReporteRetencionesRow) => `$${Number(r.totalPercepciones).toFixed(2)}`,
    },
    {
      key: 'totalPercepcionesCalculadas',
      header: 'Percepciones calculadas (control)',
      render: (r: ReporteRetencionesRow) => {
        const calculado = Number(r.totalPercepcionesCalculadas ?? 0);
        const difiere = Math.abs(calculado - Number(r.totalPercepciones)) > 0.01;
        return <span className={difiere ? 'text-amber-600 font-medium' : ''} title={difiere ? 'Difiere de lo declarado en los comprobantes' : undefined}>${calculado.toFixed(2)}</span>;
      },
    },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-800 inline">Retenciones y Percepciones</h1>
        <HelpTip
          tips={[
            'Totales de retenciones (Pagos) y percepciones (Facturas) IIBB agrupados por período, jurisdicción y tipo.',
            'El total de percepciones es lo declarado en los comprobantes; el calculado por el sistema se muestra como control.',
            'El período se calcula a partir de la fecha del pago (retenciones) o de emisión (percepciones).',
            'Exportá el reporte a CSV con el botón "Exportar CSV" de la tabla — no hay exportación TXT/agencia en esta versión.',
          ]}
        />
        <p className="text-slate-500 text-sm mt-1">Reporte agregado por período, jurisdicción y tipo</p>
      </div>
      {pestanias}

      <div className="bg-white rounded-lg border border-slate-200 p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Período (YYYY-MM)</label>
            <input
              className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm"
              placeholder="2026-07"
              value={periodo}
              onChange={e => setPeriodo(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Jurisdicción</label>
            <select
              className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm"
              value={jurisdiccionId || ''}
              onChange={e => setJurisdiccionId(Number(e.target.value))}
            >
              <option value="">Todas</option>
              {jurisdicciones.map(j => (
                <option key={j.id} value={j.id}>{j.nombre}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Tipo</label>
            <select
              className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm"
              value={tipo}
              onChange={e => setTipo(e.target.value)}
            >
              <option value="">Todos</option>
              <option value="iibb">IIBB</option>
              <option value="ganancias">Ganancias</option>
            </select>
          </div>
        </div>
      </div>

      <DataTable
        columns={columns}
        data={data ?? []}
        loading={loading}
        emptyMessage="Sin retenciones ni percepciones para los filtros seleccionados"
        title="Retenciones"
      />
    </div>
  );
}
